import { Zap } from 'lucide-react-native';
import { StyleSheet } from 'react-native';
import { Badge, Box, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../../theme/typography';
import type { PreviewToolCallLogEntry } from '../types';

/**
 * Turn a tool call's `args` into displayable key-value rows. Objects become one
 * row per key (undefined values dropped); anything else falls back to a single
 * JSON row so the value is still visible.
 */
export function formatArgRows(args: unknown): Array<{ key: string; value: string }> {
  if (args && typeof args === 'object' && !Array.isArray(args)) {
    return Object.entries(args as Record<string, unknown>)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => ({
        key,
        value: typeof value === 'string' ? value : JSON.stringify(value),
      }));
  }
  if (args === undefined || args === null) return [];
  return [{ key: '', value: JSON.stringify(args) }];
}

/**
 * Extract a stable HH:MM:SS from an ISO timestamp. Deterministic (avoids
 * locale/timezone flakiness in tests); returns the raw string if unparseable.
 */
export function formatToolCallTime(iso: string): string {
  const match = /T(\d{2}:\d{2}:\d{2})/.exec(iso);
  return match ? match[1] : iso;
}

interface PreviewToolCallCardProps {
  entry: PreviewToolCallLogEntry;
}

/**
 * One dry-run tool invocation in the preview panel: tool name, a "no effect"
 * simulation badge, the arguments as key-value rows, and the invocation time.
 * The card carries the full FLOSEAI-186 CA-10 accessibility label (name + args
 * + result) so screen-reader users get the same detail as the visual card.
 */
export function PreviewToolCallCard({ entry }: Readonly<PreviewToolCallCardProps>) {
  const { t } = useTranslation();
  const rows = formatArgRows(entry.args);
  const time = formatToolCallTime(entry.timestamp);

  const accessibilityLabel =
    `${t('voiceAgents.previewCall.toolCallPrefix')}: ${entry.name}` +
    ` — args: ${JSON.stringify(entry.args)}` +
    (entry.result !== undefined ? ` — result: ${JSON.stringify(entry.result)}` : '');

  return (
    <Box
      borderRadius="md"
      padding="sm"
      backgroundColor={colors.surface.row}
      style={styles.card}
      testID="preview-tool-call-card"
      accessible
      accessibilityLabel={accessibilityLabel}
    >
      <VStack spacing="xs">
        <HStack spacing="sm" align="center" justify="between">
          <HStack spacing="xs" align="center">
            <Zap size={14} color={colors.primary[400]} strokeWidth={2} />
            <Text variant="caption" color={colors.text.primary} style={styles.toolName}>
              {entry.name}
            </Text>
          </HStack>
          <Badge
            color="neutral"
            variant="subtle"
            size="sm"
            testID="preview-tool-call-simulated-badge"
          >
            {t('voiceAgents.previewCall.toolCallSimulated')}
          </Badge>
        </HStack>

        {rows.length > 0 && (
          <VStack spacing="xxs" style={styles.args} testID="preview-tool-call-args">
            {rows.map((row) => (
              <Text
                key={row.key || row.value}
                variant="caption"
                color={colors.text.tertiary}
                style={styles.argRow}
              >
                {row.key ? (
                  <Text variant="caption" color={colors.text.secondary} style={styles.argKey}>
                    {`${row.key}: `}
                  </Text>
                ) : null}
                {row.value}
              </Text>
            ))}
          </VStack>
        )}

        <Text
          variant="caption"
          color={colors.text.tertiary}
          style={styles.time}
          testID="preview-tool-call-time"
        >
          {time}
        </Text>
      </VStack>
    </Box>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
  },
  toolName: {
    fontWeight: '600',
  },
  args: {
    marginTop: tokens.spacing.xxs,
  },
  argRow: {
    fontSize: tokens.fontSize.xs,
    lineHeight: 16,
    fontFamily: darkPrecisionFontFamilies.metric,
  },
  argKey: {
    fontWeight: '600',
  },
  time: {
    fontSize: tokens.fontSize.xs,
    marginTop: tokens.spacing.xxs,
  },
});
